import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Row, Button, Spinner, Alert, Input } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { DirectoryFilters } from "./DirectoryFilters";
import { ResidentTable, groupRowsByUnit } from "./ResidentTable";
import { ImportRosterModal } from "./ImportRosterModal";
import { SendNoticeModal } from "./notices/SendNoticeModal";
import { canImportRoster, canSendNotices } from "./access";
import {
  getResidentDirectory,
  exportResidentDirectoryExcel,
  saveResidentContact,
  saveResidentUnit,
} from "../TenantForm/Helpers/api";
import "./residentDirectory.scss";

const PER_PAGE_OPTIONS = [25, 50, 100, 200];

// Current is the default view: current rows are editable in place, and those
// edits save to the Resident Directory's own store.
const STATUS_OPTIONS = [
  { value: "current", label: "Current" },
  { value: "past", label: "Past" },
  { value: "all", label: "All" },
];

/**
 * A vacant unit is part of the current picture (it comes from the live mastersheet), so it
 * shows under Current and All, never under Past.
 */
const matchesStatus = (row, status) => status === "all" || row.status === status;

const digitsOnly = (v) => String(v || "").replace(/\D+/g, "");

const SkeletonRows = () => (
  <div className="p-3">
    {Array.from({ length: 8 }).map((_, i) => (
      <div className="d-flex align-items-center gap-3 py-2" key={i}>
        <div className="rd-skeleton" style={{ width: 90 }} />
        <div className="rd-skeleton flex-grow-1" />
        <div className="rd-skeleton" style={{ width: 140 }} />
        <div className="rd-skeleton" style={{ width: 180 }} />
      </div>
    ))}
  </div>
);

/** Page numbers with ellipsis gaps, e.g. 1 … 4 5 6 … 20 */
const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let previous = 0;
  for (const p of sorted) {
    if (previous && p - previous > 1) out.push(`gap-${p}`);
    out.push(p);
    previous = p;
  }
  return out;
};

/** What the reader is looking at, from the loaded rows. */
const summarize = (rows) => {
  const current = rows.filter((r) => r.status === "current" && !r.vacant).length;
  const past = rows.filter((r) => r.status === "past").length;
  const vacant = rows.filter((r) => r.vacant).length;
  const units = new Set(
    rows.filter((r) => r.status === "current").map((r) => `${r.buildingName || ""}|${r.unitNo}`)
  ).size;
  return { current, past, vacant, units };
};

const ResidentDirectory = () => {
  const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || "{}");
  const userData = parsedAuthUser.userData || parsedAuthUser;
  // Only the company login imports from Excel; the server refuses everyone else, so the button
  // is not shown to them at all.
  const canImport = canImportRoster(userData);
  // Notices go out from the company login and from managers for their own properties.
  const canNotify = canSendNotices(userData);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeTab, setNoticeTab] = useState("compose");

  const [filterData, setFilterData] = useState({
    property_name: "",
    property_id: "",
    building_name: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("current");
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Same rule as the Mastersheet: a building must be picked whenever the
  // property has any.
  const isValidSelection =
    !!filterData.property_id &&
    (filterData.building_list?.length === 0 || !!filterData.building_name);

  useEffect(() => {
    let active = true;
    if (!isValidSelection) {
      setData(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    getResidentDirectory({
      property_id: filterData.property_id,
      building_name: filterData.building_name,
    })
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load residents.");
        setData(resp.data);
        setPage(1);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err.message || "Failed to load residents.");
        setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isValidSelection, filterData.property_id, filterData.building_name, refreshKey]);

  const filtered = useMemo(() => {
    const rows = data?.rows || [];
    const term = search.trim().toLowerCase();
    const termDigits = digitsOnly(search);

    const matched = rows.filter((row) => {
      if (!matchesStatus(row, status)) return false;
      if (!term) return true;
      if (String(row.name).toLowerCase().includes(term)) return true;
      if (String(row.unitNo).toLowerCase().includes(term)) return true;
      if (String(row.email || "").toLowerCase().includes(term)) return true;
      if (termDigits.length >= 3 && digitsOnly(row.phone).includes(termDigits)) return true;
      return false;
    });

    // Unit order, numeric-aware so 2 sorts before 10.
    return [...matched].sort((a, b) => {
      const an = parseFloat(a.unitNo);
      const bn = parseFloat(b.unitNo);
      if (!isNaN(an) && !isNaN(bn) && an !== bn) return an - bn;
      const byUnit = String(a.unitNo).localeCompare(String(b.unitNo), undefined, { numeric: true });
      if (byUnit !== 0) return byUnit;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [data, search, status]);

  useEffect(() => setPage(1), [search, status, perPage]);

  const summary = useMemo(() => summarize(data?.rows || []), [data]);

  const filteredGroups = useMemo(() => groupRowsByUnit(filtered), [filtered]);
  const pageCount = Math.max(1, Math.ceil(filteredGroups.length / perPage));
  const currentPage = Math.min(page, pageCount);
  const firstIndex = (currentPage - 1) * perPage;
  const pageGroups = filteredGroups.slice(firstIndex, firstIndex + perPage);
  const pageRows = pageGroups.flatMap(group => group.rows);
  // Rows are people plus one placeholder per vacant unit; the counts say which is which.
  const vacantCount = filtered.filter((r) => r.vacant).length;
  const peopleCount = filtered.length - vacantCount;
  const pagePeople = pageRows.filter((r) => !r.vacant).length;
  const countText = (shown, total) =>
    `${shown} of ${total} resident${total === 1 ? "" : "s"}` +
    (vacantCount ? ` · ${vacantCount} vacant unit${vacantCount === 1 ? "" : "s"}` : "");

  /**
   * Persist an inline contact edit to the Resident Directory store, then patch the
   * row in local state so the value survives re-filtering without a full refetch.
   * The cell reverts itself if this throws.
   */
  const handleSaveContact = async (row, patch, options = {}) => {
    const res = await saveResidentContact({
      property_id: filterData.property_id,
      building_name: row.buildingName || filterData.building_name,
      unit_no: row.unitNo,
      name: row.name,
      // Tells the server which of the unit's lists (lease holders / occupants) the person is in.
      role: row.role,
      ...patch,
      ...options,
    });
    if (!res?.success) throw new Error(res?.message || "Save failed");

    const same = (r) =>
      r.unitNo === row.unitNo &&
      r.name === row.name &&
      (r.buildingName || "") === (row.buildingName || "") &&
      r.moveIn === row.moveIn;

    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              same(r) ? { ...r, ...patch, contactSource: null } : r
            ),
          }
        : prev
    );
    return res;
  };

  /**
   * Persist a resident rename to the Resident Directory store (and the live
   * mastersheet row when there is one), then patch local rows so it survives
   * re-filtering.
   */
  const handleSaveUnit = async (row, patch) => {
    const res = await saveResidentUnit({
      property_id: filterData.property_id,
      building_name: row.buildingName || filterData.building_name,
      unit_no: row.unitNo,
      ...patch,
    });
    if (!res?.success) throw new Error(res?.message || "Save failed");

    const sameUnit = (r) =>
      r.unitNo === row.unitNo && (r.buildingName || "") === (row.buildingName || "");
    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) => {
              if (!sameUnit(r)) return r;
              if (
                patch.name_edit !== undefined &&
                r.status === "current" &&
                r.name === patch.name_edit.from
              ) {
                return { ...r, name: patch.name_edit.to };
              }
              return r;
            }),
          }
        : prev
    );
    return res;
  };

  const handleImported = (result) => {
    Swal.fire(
      "Imported",
      `${result?.applied ?? 0} unit${(result?.applied ?? 0) === 1 ? "" : "s"} updated in the Resident Directory.`,
      "success"
    );
    setRefreshKey((k) => k + 1);
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportResidentDirectoryExcel({
        property_id: filterData.property_id,
        building_name: filterData.building_name,
        property_name: filterData.property_name,
      });
      saveAs(new Blob([blob]), `Residents_${filterData.property_name || "property"}.xlsx`);
    } catch (err) {
      Swal.fire("Error", "Excel export failed.", "error");
    } finally {
      setDownloading(false);
    }
  };

  const placeLabel = filterData.property_name
    ? `${filterData.property_name}${filterData.building_name ? ` · ${filterData.building_name}` : ""}`
    : "";

  return (
    <div className="page-content resident-directory">
      <div className="container-fluid">
        <Row>
          <Col xl={12}>
            <Card className="rd-card">
              <div className="rd-head">
                <div className="rd-head-main">
                  <span className="rd-head-icon" aria-hidden="true">
                    <i className="mdi mdi-account-group-outline" />
                  </span>
                  <div className="rd-head-text">
                    <h1 className="rd-title">Resident Directory</h1>
                    <p className="rd-sub">
                      Residents, contact details and notices, organised by unit.
                    </p>
                  </div>
                </div>
                <div className="rd-head-actions" role="group" aria-label="Directory actions">
                  {canNotify && (
                    <>
                      <Button
                        color="primary"
                        className="rd-btn"
                        onClick={() => {
                          setNoticeTab("compose");
                          setNoticeOpen(true);
                        }}
                        disabled={!isValidSelection}
                        title={isValidSelection ? "Email a notice to the residents of this building" : "Pick a property and building first"}
                      >
                        <i className="mdi mdi-send-outline" aria-hidden="true" />
                        Send Notice
                      </Button>
                      <Button
                        outline
                        color="secondary"
                        className="rd-btn"
                        onClick={() => {
                          setNoticeTab("history");
                          setNoticeOpen(true);
                        }}
                        disabled={!isValidSelection}
                        title="Notices sent to this building"
                      >
                        <i className="mdi mdi-history" aria-hidden="true" />
                        Notices
                      </Button>
                    </>
                  )}
                  {canImport && (
                    <Button
                      outline
                      color="secondary"
                      className="rd-btn"
                      onClick={() => setImportOpen(true)}
                      disabled={!isValidSelection}
                    >
                      <i className="mdi mdi-tray-arrow-up" aria-hidden="true" />
                      Import Excel
                    </Button>
                  )}
                  <Button
                    outline
                    color="secondary"
                    className="rd-btn"
                    onClick={handleExport}
                    disabled={downloading || !data?.rows?.length}
                  >
                    {downloading ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-tray-arrow-down" aria-hidden="true" />
                    )}
                    Download Excel
                  </Button>
                </div>
              </div>

              <div className="rd-filters">
                <DirectoryFilters
                  userData={userData}
                  filterData={filterData}
                  setFilterData={setFilterData}
                />
              </div>

              <CardBody className="rd-body">
                {!isValidSelection && (
                  <div className="rd-empty">
                    <span className="rd-empty-icon" aria-hidden="true">
                      <i className="mdi mdi-office-building-marker-outline" />
                    </span>
                    <p className="rd-empty-title">
                      {filterData.property_id ? "Pick a building" : "Pick a property to start"}
                    </p>
                    <p className="rd-empty-text">
                      {filterData.property_id
                        ? "Choose a building above to see who lives there."
                        : "Residents are listed per building, with current people editable in place."}
                    </p>
                  </div>
                )}

                {error && <Alert color="danger">{error}</Alert>}

                {loading && <SkeletonRows />}

                {!loading && data && (
                  <>
                    <div className="rd-summary" aria-label="Summary">
                      <span className="rd-summary-place">{placeLabel}</span>
                      <span className="rd-summary-stat">
                        <strong>{summary.current}</strong> current resident{summary.current === 1 ? "" : "s"}
                      </span>
                      <span className="rd-summary-stat">
                        <strong>{summary.units}</strong> unit{summary.units === 1 ? "" : "s"}
                      </span>
                      <span className={`rd-summary-stat${summary.vacant ? " is-vacant" : ""}`}>
                        <strong>{summary.vacant}</strong> vacant
                      </span>
                      <span className="rd-summary-stat">
                        <strong>{summary.past}</strong> past
                      </span>
                    </div>

                    <div className="rd-toolbar">
                      <div className="rd-search">
                        <label htmlFor="rd-search" className="visually-hidden">
                          Search residents
                        </label>
                        <i className="mdi mdi-magnify rd-search-icon" aria-hidden="true" />
                        <Input
                          id="rd-search"
                          type="search"
                          value={search}
                          placeholder="Search name, unit, email or phone"
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>

                      <div className="rd-segment" role="group" aria-label="Resident status">
                        {STATUS_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className="rd-segment-btn"
                            aria-pressed={status === option.value}
                            onClick={() => setStatus(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="rd-result-count" aria-live="polite">
                      Showing {countText(pagePeople, peopleCount)}.
                      {status === "past"
                        ? " Past residents are historic records and cannot be edited."
                        : " Click a name, number or email to edit it; you will be asked to confirm before it saves."}
                    </div>

                    {filtered.length === 0 ? (
                      <div className="rd-empty rd-empty-compact">
                        <p className="rd-empty-title">
                          {search || status !== "current"
                            ? "No residents match these filters."
                            : "No current residents recorded for this building yet."}
                        </p>
                        {(search || status !== "current") && (
                          <Button
                            color="link"
                            onClick={() => {
                              setSearch("");
                              setStatus("current");
                            }}
                          >
                            Reset filters
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                        <ResidentTable key={`${filterData.property_id}|${filterData.building_name}`}
                          rows={pageRows}
                          onSaveContact={handleSaveContact}
                          onSaveUnit={handleSaveUnit}
                        />

                        <div className="rd-footer">
                          <span>
                            Showing {firstIndex + 1} to {firstIndex + pageGroups.length} of {filteredGroups.length} unit groups
                          </span>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <label htmlFor="rd-perpage" className="visually-hidden">
                              Unit groups per page
                            </label>
                            <Input
                              id="rd-perpage"
                              type="select"
                              className="rd-perpage"
                              value={perPage}
                              onChange={(e) => setPerPage(Number(e.target.value))}
                            >
                              {PER_PAGE_OPTIONS.map((n) => (
                                <option key={n} value={n}>
                                  {n} units per page
                                </option>
                              ))}
                            </Input>

                            <nav aria-label="Resident pages" className="d-flex align-items-center gap-1">
                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="First page"
                                disabled={currentPage === 1}
                                onClick={() => setPage(1)}
                              >
                                <i className="mdi mdi-chevron-double-left" />
                              </button>
                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="Previous page"
                                disabled={currentPage === 1}
                                onClick={() => setPage(currentPage - 1)}
                              >
                                <i className="mdi mdi-chevron-left" />
                              </button>

                              {pageWindow(currentPage, pageCount).map((entry) =>
                                typeof entry === "number" ? (
                                  <button
                                    key={entry}
                                    type="button"
                                    className="rd-page-btn"
                                    aria-label={`Page ${entry}`}
                                    aria-current={entry === currentPage ? "page" : undefined}
                                    onClick={() => setPage(entry)}
                                  >
                                    {entry}
                                  </button>
                                ) : (
                                  <span key={entry} className="rd-page-gap" aria-hidden="true">
                                    ...
                                  </span>
                                )
                              )}

                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="Next page"
                                disabled={currentPage === pageCount}
                                onClick={() => setPage(currentPage + 1)}
                              >
                                <i className="mdi mdi-chevron-right" />
                              </button>
                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="Last page"
                                disabled={currentPage === pageCount}
                                onClick={() => setPage(pageCount)}
                              >
                                <i className="mdi mdi-chevron-double-right" />
                              </button>
                            </nav>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {canImport && (
        <ImportRosterModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          filterData={filterData}
          onImported={handleImported}
        />
      )}

      {canNotify && noticeOpen && (
        <SendNoticeModal
          isOpen={noticeOpen}
          onClose={() => setNoticeOpen(false)}
          filterData={filterData}
          userData={userData}
          initialTab={noticeTab}
        />
      )}
    </div>
  );
};

export { ResidentDirectory };
